import { BLOCK_KINDS, type BlockKind } from '@personalizehq/editor';

export interface BlockPreset {
  label: string;
  kind: BlockKind;
  defaults?: Record<string, unknown>;
  requires?: 'trackable_scroll';
  /** This preset cannot be materialized from the palette alone — it needs the author to choose
   *  something first. `icon` opens the icon picker, which resolves a glyph to a managed asset and
   *  THEN inserts at the remembered target. Insertion is otherwise synchronous everywhere, so the
   *  flag lives on the preset rather than being inferred from the block kind: an `image` inserted
   *  from the palette is still an ordinary placeholder image. */
  picks?: 'icon';
}

export type DragItem = { type: 'node'; id: string } | { type: 'block'; preset: BlockPreset };

const MIME = 'application/x-personalizehq-builder';
let active: DragItem | null = null;

export function writeDragItem(data: DataTransfer, item: DragItem): void {
  active = item;
  data.effectAllowed = item.type === 'node' ? 'move' : 'copy';
  data.setData(MIME, JSON.stringify(item));
}

export function readDragItem(data: DataTransfer): DragItem | null {
  const value = data.getData(MIME);
  if (!value) return active;
  try {
    const item = JSON.parse(value) as { type?: unknown; id?: unknown; preset?: Partial<BlockPreset> };
    if (item.type === 'node' && typeof item.id === 'string') return { type: 'node', id: item.id };
    if (
      item.type === 'block' &&
      typeof item.preset?.label === 'string' &&
      BLOCK_KINDS.includes(item.preset.kind as BlockKind)
    ) {
      return {
        type: 'block',
        preset: {
          label: item.preset.label,
          kind: item.preset.kind as BlockKind,
          ...(item.preset.defaults && typeof item.preset.defaults === 'object' ? { defaults: item.preset.defaults } : {}),
          ...(item.preset.requires === 'trackable_scroll' ? { requires: item.preset.requires } : {}),
          ...(item.preset.picks === 'icon' ? { picks: item.preset.picks } : {}),
        },
      };
    }
    return null;
  } catch {
    return null;
  }
}

export function clearDragItem(): void {
  active = null;
}
